import React, { useContext, useState, useRef, useEffect, useCallback, forwardRef} from 'react';

import {
  IconButton,
  Box,
  Button
} from '@mui/material';

import AlertNotification from '../components/AlertNotification';
import PdfViewerComponent from '../components/PdfViewerComponent';
import ToggleSplitPane from '../components/ToggleSplitPane';
import PageLoader from '../components/PageLoader';

import {
    TextFieldWithIconButton,
    CustomAutoComplete,
    DocumentUtility,
    UtilityLibrary,
    batchDataService,
    BusinessUtility,
    ApiManager,
    sessionService,
    SessionManager,
    EnumUtility,
    LoggerUtility,
    ConfirmationDialog,
    NotificationDialog,
    DeterminePermissions,
    CaseDetailManager,
    ClientType,
    SharedService
} from '../components/ComponentsLibrary';

import { useConfigContext } from '../functions/ConfigContext';



const CaseView = forwardRef((props, ref) => {

  const leftPaneTitle = "Case Information";
  const rightPaneTitle = "Document Viewer"

  const errorFormName = "Case View";
  const caseUniqueID = "caseNumber";

  const { configLibrary } = useConfigContext();
  const masterServerDBSchema = SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.masterDBSchema;
  const { siteID, stateID, legacyUserID: userLoginID } = SessionManager.GetDataFromSession("LoginUserInfoKey") || {};
  // Validation patterns
  const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey));
  const caseNumberRegex = siteSettingDetail.CaseNumberRegex || { caseRegexValue: "", caseRegexMessage: "" };
  // Create a ref for the child component
  const pdfViewerRef = useRef(null);
  const caseNumberRef = useRef();

// UI based states  
  const [alertProps, setAlertProps] = useState({
    type: '',
    message: '',
    open: false,
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);
  const [pendingEditorCloseAction, setPendingEditorCloseAction] = useState(null);

  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationDialogContent, setNotificationDialogContent] = useState([]);
  const [notificationDialogPosition, setNotificationDialogPosition] = useState("center");
  const [notificationDialogTimer, setNotificationDialogTimer] = useState(0);
  const [notificationAutoClose, setNotificationAutoClose] =useState(false)
  
  const [isPageLoading, setIsPageLoading] = useState(false);

  const initialCaseNumberIconsDisabled = { search: true, copy: true, clear: true };
  const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);
  const [enableCaseSearch, setEnableCaseSearch] = useState(false);
  const [enableCaseClear, setEnableCaseClear] = useState(false);

    // functional states
  const [inputCaseNumber, setInputCaseNumber] = useState(null);
  const [showCaseInfo, setShowCaseInfo] = useState(false);
  const [caseData, setCaseData] = useState({});
  const [currentCaseDetailID, setCurrentCaseDetailID] = useState('DocumentCaseDetail')
  const [selectedDoc, setSelectedDoc] = useState({})

  const caseDetailOptionsForControls = [
    { id: 1, type: "Document", label: "Documents", value: "DocumentCaseDetail" },
    { id: 2, type: "WorkflowActivity", label: "Workflow Activities", value: "WorkflowActivityCaseDetail" },
    { id: 3, type: "CustomerServiceContacts", label: "Customer Service Contacts", value: "CustomerServiceContacts" },
    { id: 4, type: "Workflows", label: "Workflows", value: "WorkflowsCaseDetail" },
  ];

  const customDateFormat = "MM/DD/YYYY hh:mm A";

  // permissions states
  const defaultPermissionsForFields = {//TODO remove unnecessary
    caseNumberTxt: { read: false, write: true, hide: false },
    caseSearchDgrd: { read: false, write: true, hide: false },
    caseNumberLbl: { read: false, write: true, hide: false },
    cpNameLbl: { read: false, write: true, hide: false },
    ncpNameLbl: { read: false, write: true, hide: false },
    statusTypeLbl: { read: false, write: true, hide: false },
    caseDetailSelect: { read: false, write: true, hide: false },
    clearFiltersBtn: { read: false, write: true, hide: false },
    exportDocumentsBtn: { read: false, write: true, hide: false },
    documentsDgrd: { read: false, write: true, hide: false },
    cancelBtn: { read: false, write: true, hide: false },
    searchBtn: { read: false, write: true, hide: false },
    documentViewerVwr: { read: false, write: true, hide: false },
    docVwr1Editor: { read: false, write: true, hide: false },
    docVwr1ToolbarItems: { read: false, write: true, hide: false },
    docVwr2Editor: { read: false, write: true, hide: false },
    docVwr2ToolbarItems: { read: false, write: true, hide: false },
    docVwrRotateBtn: { read: false, write: true, hide: false },
    docVwrSplitBtn: { read: false, write: true, hide: false },
    docVwrDeleteBtn: { read: false, write: true, hide: false },
    docVwrSaveBtn: { read: false, write: true, hide: false },
  };
  const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

  const documentViewerAccessPermissions = {
    viewOnlyAccess: true,
    editorAccess: formFieldAccess.docVwr1Editor,
    viewerToolbarItemsAccess: formFieldAccess.docVwr1ToolbarItems,
    rotateButtonAccess: formFieldAccess.docVwrRotateBtn,
    splitButtonAccess: formFieldAccess.docVwrSplitBtn,
    deleteButtonAccess: formFieldAccess.docVwrDeleteBtn,
    saveButtonAccess: formFieldAccess.docVwrSaveBtn,
  };

  
  // useEffects 
  useEffect(() => {
    document.title = "YTrac :: Case View";
  }, []);

  const toUpperCase = (stringToUpperCase) => {
    return String(stringToUpperCase).toUpperCase();
  };
  

  //=======================================================================
  // Get Methods - Begin
  //=======================================================================

  const caseInfoMapping = {
        
    caseNumber: ["CaseNumber", "caseNumber"],
    caseFound: "caseFound",
    caseStatus: "caseStatus",
    caseType: ["CaseType", "caseType"],
    caseTypeID: "",
    caseWorker: ["CaseworkerName", "caseWorker", "caseworkerName"],
    countyID: ["CountyID", "countyID"],
    countyName: "",
    cpFirstName: ["CPFirstName", "cpFirstName"],
    cpFullName: ["CPName", "cpFullName"],
    cpLastName: ["CPLastName", "cpLastName"],
    cpssn: ["CPSSN", "cpssn"],
    createdTS: "",
    districtID: ["DistrictID", "districtID"],
    familyViolence: ["FamilyViolence", "familyViolence"],
    fVIAndConfidentialCase: "FVIAndConfidentialCase",
    confidentialCase: "ConfidentialCase",
    fKM_UserLoginID: "",
    ncpFirstName: ["NCPFirstName", "ncpFirstName"],
    ncpFullName: ["NCPName", "ncpFullName"],
    ncpLastName: ["NCPLastName", "ncpLastName"],
    ncpssn: ["NCPSSN", "ncpssn"],
    restrictedStatusMessage: "RestrictedStatusMessage",
    specialCircumstanceContent: "",
    specialCircumstanceContentActive: "",
    statusType: ["CaseStatus", "statusType"],   // This looks like a problem.
    statusTypeID: "",
    yTracCaseID: "yTracCaseID",
  };


  const getCaseInfoFromApiByID = async (caseID)=> {
    try {
      const params = {
        caseNumber: caseID,
        apiMainframeUrl: configLibrary.apiMainframeUrl,
        masterServerDBSchema
      };
            
      const caseApiData = await ApiManager.GetCaseDataFromDbByID(params)
            
      if (Array.isArray(caseApiData.caseInfo) && caseApiData.caseInfo.length > 0) {
        const mappedCaseInfoData = caseApiData.caseInfo.map(item => {
          const mappedItem = UtilityLibrary.mapApiResponseToObject(item, caseInfoMapping);
          mappedItem.lastViewed = new Date().toISOString();
          return mappedItem;
        })

        return {
          caseInfo: mappedCaseInfoData[0],
          statusCode: caseApiData.statusCode,
          errorMessage: caseApiData.errorMessage
        };
      }
      else if (!Array.isArray(caseApiData.caseInfo) && caseApiData.caseInfo) {
        const caseInfoData = UtilityLibrary.mapApiResponseToObject(caseApiData.caseInfo, caseInfoMapping);
        caseInfoData.lastViewed = new Date().toISOString();
                
        return {
          caseInfo: caseInfoData,
          statusCode: caseApiData.statusCode,
          errorMessage: caseApiData.errorMessage
        };
      }
      else {
        return {
          caseInfo: null,
          statusCode: caseApiData.statusCode,
          errorMessage: caseApiData.errorMessage
        };
      }
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "getCaseInfoFromApiByID", {});
    }
  };

  const getCaseDataByIDWrapper = async (caseID) => {
    try {
      let hasCaseData = true;
      let error = false;
      let dialogContent = null;
      const caseInfoData = await getCaseInfoFromApiByID(caseID);
      let caseData = caseInfoData.caseInfo;
      if (caseData?.caseNumber) {
        setCaseData(caseData);
      }
      else {
        hasCaseData = false;
        error = true;
        const params = {
            StateID: stateID,
            MessageContentID: ClientType.MessageContent.E_UI7053_Case_Id_not_Found,
        };
        const messageContentData = await ApiManager.GetMessageContentFromApi(params);
        dialogContent = {
          NotificationType: 'error',
          Message: messageContentData?.messageText,
        };
      }
      return { hasCaseData, statusCode: caseInfoData.statusCode, error, errorMessage:  dialogContent};
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "getCaseDataByIDWrapper", {});
    }
  };

  //=======================================================================
  // Get Methods - End
  //=======================================================================



  //=======================================================================
  // Action Events - Begin
  //=======================================================================

  const simulateAsyncDelay = async () => {
    await new Promise((resolve) => setTimeout(resolve, 0));
  }
   
  const checkCaseNumberRegEx = async (caseNumber) => {// RC removed async as its not needed
    try {
      let isValidFormat = true;
      const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");
    
      if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
        if (!caseNumberRegexValue.test(caseNumber)) {
          caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
          changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
          isValidFormat = false;
        }
        else {
          caseNumberRef.current.setValidationMessage("");
          changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
        }
      }
      else {
        caseNumberRef.current.setValidationMessage("");
        changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
      }
    
      return isValidFormat;
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
    }
  };

  const handleCaseSearch = async (event) => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      
      // Clear previous case data and document selection before starting new search
      setShowCaseInfo(false);
      setCaseData({});
      setSelectedDoc({});
      
      const isValidCaseFormat = await checkCaseNumberRegEx(inputCaseNumber);
      if (!isValidCaseFormat) {
        setShowCaseInfo(false)
      }
      else {
        setInputCaseNumber(inputCaseNumber)
        const caseID = toUpperCase(inputCaseNumber);
        const { hasCaseData, error, errorMessage } = await getCaseDataByIDWrapper(caseID);
        console.log(hasCaseData, error, errorMessage)
        if (error) {
          handleNotificationDialog(errorMessage);
          setShowCaseInfo(false)
        }
        else {
          setShowCaseInfo(true)
        }
      }
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
    }
    finally { setIsPageLoading(false); }
  };

  // Function to handle clear of case search value
  const handleCaseClear = async () => {
    setIsPageLoading(true);
    try {
      await simulateAsyncDelay();
      setInputCaseNumber("");
      setShowCaseInfo(false);
      setSelectedDoc({});
      setCaseData({});
      setCaseNumberIconsDisabled({ search: true, copy: true, clear: true });
      setEnableCaseSearch(false);
      setEnableCaseClear(false);
      caseNumberRef.current.setValidationMessage("");
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseClear");
    }
    finally { setIsPageLoading(false); }
  };


  const changeCaseNumberIconsDisable = (iconsEnabledValue) => {// RC removed async as its not needed
    try {
      const updatedCaseNumberIconsEnabled = {
        ...caseNumberIconsDisabled,
        search: iconsEnabledValue.search,
        copy: iconsEnabledValue.copy,
        clear: iconsEnabledValue.clear,
      };
      setCaseNumberIconsDisabled((prevValues) => ({
        ...prevValues,
        ...updatedCaseNumberIconsEnabled
      }));
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
    }
  };

  const handleCaseChange = async (e) => {
    try {
      // RC do not make any async calls before case number 
      // state update to ensure the case number cursor stays 
      // in place while editing
      const valueText = e.target.value;
      if (!enableCaseClear && valueText.trim() !== "") setEnableCaseClear(true);
      let isValidCaseFormat = true;
      const regexString = caseNumberRegex?.caseRegexValue?.trim() || "";
      if (regexString) {
          const caseNumberRegexValue = new RegExp(regexString);
           isValidCaseFormat = caseNumberRegexValue.test(valueText)
      }
      caseNumberRef.current.setValidationMessage(!isValidCaseFormat && valueText != "" ? caseNumberRegex.caseRegexMessage : "");
      changeCaseNumberIconsDisable({ search: !isValidCaseFormat, copy: false, clear: false });
      setEnableCaseSearch(isValidCaseFormat)
      // Update input value
      setInputCaseNumber(valueText);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
    }
  };


  const handleAlertClose = () => {
    setAlertProps((prevState) => ({ ...prevState, open: false }));
  };


  const handleCaseDetailInternalChange = (caseID) => (event, newValue, reason) => {
    setCurrentCaseDetailID(newValue.value);
  };

  //=======================================================================
  // Action Events - End
  //=======================================================================



  //=======================================================================
  //  Methods - Begin
  //=======================================================================
  const handleNotificationDialog = (dialogContent, timerDuration = 0, position = "center") => {
    try {
      setNotificationDialogContent(dialogContent);
      setNotificationDialogPosition(position);
      setNotificationDialogTimer(timerDuration);
      if (timerDuration) {
          setNotificationAutoClose(true);
        }
        else {
        setNotificationAutoClose(false);
      }
      handleOpenNotificationDialog();
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "handleNotificationDialog", {});
    }
  };

  const handleOpenNotificationDialog = () => {
    try {
      setNotificationDialogOpen(true);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "handleOpenNotificationDialog", {});
    }
  };

  const handleCloseNotificationDialog = useCallback(() => {
    try {
      setNotificationDialogOpen(false);
    }
    catch (error) {
      throw LoggerUtility.ProcessErrorData(error, "handleCloseNotificationDialog", {});
    }
  }, []);

  //=======================================================================
  // Confirmation Dialog - Begin
  //=======================================================================

  const handleConfirmationDialog = (dialogContent) => {
    setConfirmationDialogContent(dialogContent);
    setConfirmDialogOpen(true);
  };

  const handleCloseConfirmDialog = () => {
    setPendingEditorCloseAction(null);
    setConfirmDialogOpen(false);
  };

  const handleDialogConfirmAction = (dialogContentID) => {
    switch (dialogContentID) {
      case EnumUtility.ConfirmDialogType.CloseDocumentEditor.ID:
        if (pendingEditorCloseAction) {
          pendingEditorCloseAction();
          setPendingEditorCloseAction(null);
        }
        break;

      default:
        break;
    }

    setConfirmDialogOpen(false);
  };

  const handleDocumentEditorCloseRequest = ({ hasSelectedPages, closeEditor }) => {
    if (!hasSelectedPages) {
      closeEditor();
      return false;
    }

    setPendingEditorCloseAction(() => closeEditor);
    handleConfirmationDialog(EnumUtility.ConfirmDialogType.CloseDocumentEditor);
    return false;
  };

  //=======================================================================
  // Confirmation Dialog - End
  //=======================================================================


  /* 
  * Authorization code starts here
  * The permission values for fields are received from Web API
  * The received data from Web API response is processed and stored in a state variable "formFieldAccess"
  */
  useEffect(() => {
    const fetchFormFieldPermissions = async () => {
      try {
        const loginUserInfo = sessionService.getUser();
        const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
        const params = {
          clientID: loginStateID,
          formID: 40      // To-do: Modify Code - Hard-coded value of FormID: 40
        }
        const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
        const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
        setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields});
      } catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "fetchFormFieldPermissions", {});
      }
    };

    try {
      fetchFormFieldPermissions();
    }
    catch (error) {
      LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions - Page Load");
    }
  }, []);

  //=======================================================================
  //  Methods - End
  //=======================================================================


  //=======================================================================
  // ROTATE - Save Document Process - Begin
  //=======================================================================

  // Handler for the save rotate button press
  const handleSaveRotateButtonPress = async (event, { setOperations, getSelectedPageIndexes }) => {
    try {
      // const { documentBuffer, batchInstance } = await BusinessUtility.PerformSaveAfterRotateOperation(getBatchDocument.BatchDocumentID, setOperations, getSelectedPageIndexes);
      console.log("args to prformsave after rotate", "1", pdfViewerRef, "2", selectedDoc.batchDocumentID, "3", setOperations, "4", getSelectedPageIndexes)
      const result = await BusinessUtility.PerformSaveAfterRotateOperation(pdfViewerRef, selectedDoc.batchDocumentID, setOperations, getSelectedPageIndexes);
      /*  
        if (pdfViewerRef.current) {
            await pdfViewerRef.current.handleDocumentExport(documentBuffer, batchInstance);
        } */

      // Show appropriate success message based on whether operations were performed
      if (result.hasOperations) {
          handleNotificationDialog(EnumUtility.BatchDocumentProcess.DocumentRotateSuccess);
      } 
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  //=======================================================================
  // ROTATE - Save Document Process - End
  //=======================================================================

 
  //=======================================================================
  // Save As Process - Begin
  //=======================================================================

  // Handler for the save as button press
  const handleSaveAsButtonPress = async () => {

    const docSplitData = batchDataService.getSplitDocumentData();

    // Export the document as a Blob
    const buffer = await docSplitData.batchInstance.exportPDF();
    const blob = new Blob([buffer], { type: "application/pdf" });

    const paramsBatchDoc = {
        batchDocumentID: selectedDoc.batchDocumentID, //getBatchDocument.BatchDocumentID,
    };
    const batchDocApiData = await ApiManager.GetBatchDocumentByIDFromApi(paramsBatchDoc);
    console.log("batchDocAPi response", batchDocApiData ,"for selected Doc", paramsBatchDoc)

    let docLocation = await BusinessUtility.replaceExtensionWithPdf(batchDocApiData.documentLocation);
    if (!docLocation || docLocation.trim().length === 0) {
        docLocation = 'document.pdf' 
    }

    try {
    const handle = await window.showSaveFilePicker({
        suggestedName: docLocation, // Default file name
        types: [
        {
            description: 'PDF Files',
            accept: { 'application/pdf': ['.pdf'] },
        },
        ],
    });

    // Create a writable stream and write the Blob to it
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();

    console.log("Document saved successfully.");
    } catch (error) {
    console.error("Error saving document:", error);
    }
  };


  const handlePDFViewerError = async(errorMessage, documentDetails) => {
    const loginUserInfo = sessionService.getUser();
    const { stateName, firstName, lastName} = loginUserInfo
    const { batchDocumentID, batchID } = selectedDoc
    const currentDate = UtilityLibrary.formatDate(new Date(), customDateFormat);
    
    const dialogMessage = `Document Preview Failed. Please contact Helpdesk.
    Exception = ${errorMessage}
    Date/Time of Error = ${currentDate}
    Page = 'View-Case(s)'
    State = ${stateName}
    User = ${firstName} ${lastName}
    Region =  ${configLibrary.hostedEnvironment}            
    CaseNumber = ${caseData?.caseNumber}
    Batch ID = ${batchID}
    Document ID = ${batchDocumentID}`;
    
    handleNotificationDialog({ NotificationType: "error", Message: dialogMessage })
  }
  
  const handleCopyPathButtonPress = async () => {
        try {
        //selectedDoc.DocumentUNCPath
      const loginUserInfo = sessionService.getUser();
      const { stateID, siteID, legacyUserID: userID } = loginUserInfo || {};
      // Get temp file set det
      const setDetailIDs = [ClientType.SettingsDetail.DMS_Scanning__Copy_Link_Temp_Path,
      ClientType.SettingsDetail.UI_Miscellaneous__Success_Notification_TIMER]
      const params = {
        settingsDetailIDList: setDetailIDs.join(","),
        yTracSiteID: stateID
      }
      const settingDetails = await ApiManager.GetSettingsDetailValuesFromApi(params); 
      const messageContentData = await SharedService.getMessageContent(ClientType.MessageContent.N_UI7057_Document_Path_Copied);
      const timerValue = (settingDetails?.settingDetailsApiData.find(
        value => parseInt(value.settingsDetailID) === ClientType.SettingsDetail.UI_Miscellaneous__Success_Notification_TIMER)?.settingsDetailValue || 3) * 1000;
      await SharedService.handleCopyPathButtonPress(selectedDoc, selectedDoc?.uncExternalDocumentUrl, settingDetails, { stateID, siteID, userID }, masterServerDBSchema);
        // Display notification dialog
      handleNotificationDialog({ NotificationType: "success", Message: messageContentData?.messageText}, timerValue, "top-right"
      )
    }
    catch (err) {
      handleNotificationDialog({ NotificationType: "error", Message: 'Error occured while coping the file path' }, 3000, "top-right"
      );
    }   
  } 

  //=====================================================================
  // Save As Process - End
  //=======================================================================

  // JSX
  const mainContent = (
    <div className="content-main-container"
     >
      <div className="pane-wrap-container"
       >
        <div className="pane-main-container">
          <div className="form-section">
            <div className="form-main-container"
              style={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%'
                }}>
            
              <div className="form-content"
                style={{
                  flex: 1,
                  overflowY: 'auto'
              }}
              >
                <div className="form-dynamic-content">
                  <Box>
                    {/* Content Section */}
                    <Box p={2}>
                      <div style={{ padding: '16px' }}>
                        {/* Search TextField */}
                        {!(formFieldAccess.caseNumberTxt?.hide ?? false) && (
                          <TextFieldWithIconButton
                            ref={caseNumberRef}
                            nameForField="caseNumberTxt"
                            onIconButtonClick={handleCaseSearch}
                            onClearButtonClick={handleCaseClear}
                            onTextChange={handleCaseChange}
                            valueForField={inputCaseNumber}
                            labelForField="Case Search"
                            iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                            isDisabled={!(formFieldAccess.caseNumberTxt?.write ?? true)}
                            iconsDisabled={caseNumberIconsDisabled}
                            disableKeyDown={caseNumberIconsDisabled.search}
                          />
                        )}
                      </div>
                                    
                      {(showCaseInfo && caseData?.caseNumber) &&
                        <div>
                          {/* Line separator after case number search */}
                        <div className='row' style={{ padding: "4px"}}>
                          <div className='col-12' style={{ padding: "2px", borderBottom: '2px solid grey'}}>
                          </div>
                        </div>
                        {/* Sticky Case Information Section */}
                        <div 
                          style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            backgroundColor: 'white',
                            padding: '16px 16px 0px',
                            width: '100%'
                          }}
                        >
                          {/* Top Section (Yellow) */}
                          <div className="form-caseInfo-container" style={{ paddingBottom: '16px'}}>
                              {!(formFieldAccess.caseNumberLbl?.hide ?? false) && (
                              <div className="form-caseInfo-field">
                                <label className="text-wrapper-subtitle caseInfo-Title">Case ID:</label>
                                <label id="caseNumberLbl" name="caseNumberLbl" className="text-wrapper-subtitle caseInfo-Content">{caseData.caseNumber || ''}</label>
                              </div>
                              )}
                              {!(formFieldAccess.cpNameLbl?.hide ?? false) && (
                                <div className="form-caseInfo-field">
                                  <label className="text-wrapper-subtitle caseInfo-Title">CP:</label>
                                  <label id="cpNameLbl" name="cpNameLbl" className="text-wrapper-subtitle caseInfo-Content">{(caseData.cpFirstName || ' ') + ' ' + (caseData.cpLastName || ' ')}</label>
                                </div>
                              )}
                              {!(formFieldAccess.ncpNameLbl?.hide ?? false) && (
                                <div className="form-caseInfo-field">
                                  <label className="text-wrapper-subtitle caseInfo-Title">NCP:</label>
                                  <label id="ncpNameLbl" name="ncpNameLbl" className="text-wrapper-subtitle caseInfo-Content">{(caseData.ncpFirstName || '') + ' ' + (caseData.ncpLastName || '')}</label>
                                </div>
                              )}
                            </div>
                              <div className="form-caseInfo-container">
                              {!(formFieldAccess.caseDetailSelect?.hide ?? false) && (
                                <div style={{ width: '100%' }}>
                                  <CustomAutoComplete
                                    customVariant='blueBorderVariant'
                                    idForControl='caseDetailSelect'
                                    labelForControl='Case Detail'
                                    valueForControl={currentCaseDetailID}
                                    onChange={handleCaseDetailInternalChange(caseData.caseNumber)}
                                    optionsSource={caseDetailOptionsForControls}
                                    multiple={false}
                                    getOptionLabel={(option) => option?.label || ''}
                                    disableCloseOnSelect={false}
                                    isDisabled={false}
                                    noOptionsText='Case Detail Not Found'
                                  />
                                </div>
                              )}
                          </div>
                          <div className='row' style={{ padding: "8px" }}>
                            <div className='col-12' style={{ padding: "2px", borderBottom: '2px solid grey' }}>
                            </div>
                          </div>
                        </div>

                        {/* Scrollable Content Area */}
                        <div style={{ overflowY: 'auto' }}>
                        <div>
                            {showCaseInfo && (
                              currentCaseDetailID && (
                                <CaseDetailManager
                                  PrimaryID={caseData.caseNumber}
                                  SelectedCaseDetailID={currentCaseDetailID}
                                  SetSelectedDoc={setSelectedDoc}
                                  SelectedDoc={selectedDoc}
                                  displayDocumentInternally={false}
                              />
                            ))}
                        </div>
                        </div>
                      </div>
                      }
                    </Box>
                  </Box>
              </div>
              </div>
              {/* Footer Section */}
              <footer className="form-footer"
                style={{
                  flexShrink: 0,
                  marginTop: 'auto'
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                  {!(formFieldAccess.clearBtn?.hide ?? false) && (
                  <div>
                    <Button id="clearBtn" name="clearBtn" color="ywButtonWhite" variant='outlined' disabled={!enableCaseClear} onClick={handleCaseClear}>CLEAR</Button>
                  </div>
                  )}
                  <div style={{ display: "flex", gap: "18px" }}>
                    {!(formFieldAccess.searchBtn?.hide ?? false) && (
                      <Button id="saveBtn" name="saveBtn" color="ywButtonBlack" variant='contained' className='me-2' disabled={!enableCaseSearch} onClick={handleCaseSearch}>SEARCH</Button>
                    )}
                  </div>
                </div>
              </footer>
            </div >
        </div>
        </div>
      </div>
    
    </div >
  );
  const rightPaneContent =
    showCaseInfo && selectedDoc.uncExternalDocumentUrl ? (<div style={{ width: '100%', height: '100%' }}>
      <PdfViewerComponent
        ref={pdfViewerRef}
        origin="CaseInformation"
        id="documentViewerBatchVwr"
        name="documentViewerBatchVwr"
        key={selectedDoc.uncExternalDocumentUrl}
        document={selectedDoc.uncExternalDocumentUrl}
        onSaveRotateButtonPress={handleSaveRotateButtonPress}
        onSaveAsButtonPress={handleSaveAsButtonPress}
        viewerAccessPermissions={documentViewerAccessPermissions}
        onCopyPathButtonPress={handleCopyPathButtonPress}
        style={{ width: '90%' }}
        onPDFReadError={handlePDFViewerError}
        onEditorCloseRequest={handleDocumentEditorCloseRequest}
      />
    </div>): null
  
    return (
      <div style={{ height: '100%' }}>
         <ToggleSplitPane
            leftPaneVisibility={true}
            leftPaneTitle={leftPaneTitle}
            leftPaneContent={mainContent}
            rightPaneTitle={rightPaneTitle}
            rightPaneContent={rightPaneContent}
            leftPaneSize='50%'
          >
          </ToggleSplitPane>

          <AlertNotification
            alertType={alertProps.type}
            message={alertProps.message}
            open={alertProps.open}
            onClose={handleAlertClose}
          />

          {/* Include the Confirmation Dialog component */}
          <ConfirmationDialog
            openConfirmation={confirmDialogOpen}
            dialogContent={confirmationDialogContent}
            onCloseConfirmation={handleCloseConfirmDialog}
            onConfirmDialog={(param) => handleDialogConfirmAction(param)}
          />

          {/* Include the Notification Dialog component */}
          <NotificationDialog
            openNotification={notificationDialogOpen}
            dialogContent={notificationDialogContent}
            onCloseNotification={handleCloseNotificationDialog}
            isAutoClose={notificationAutoClose || false}
            position={notificationDialogPosition || "center"}
            timerDuration={notificationDialogTimer || 0}
          />

          {/* Include the loader component */}
          <PageLoader isPageLoading={isPageLoading} />
      </div>
    );
});

export default CaseView;